import { useCallback, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";

import {
  useAccountData,
  useCachedDisplayData
} from "../../../helpers/firebase";
import { getAccountId } from "../../../helpers/accountHelpers";
import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import {
  trackPdfReportDownloaded,
  trackPdfReportFailed
} from "../../../helpers/analytics";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import useExplorerLayoutContext from "../../../helpers/contexts/explorerLayout";

import {
  buildCompletenessSummary,
  buildScopeSeries,
  buildSourceSeries,
  computeTotalTons,
  filterEmissionsForYear,
  findSurveyEntry,
  resolveDisplayTotalTons
} from "../views/dashboardHelpers";
import { computeSurveyProgress } from "../scout/scoutOpeners";

import PdfReportRenderer, {
  TIMEOUT_MESSAGE,
  DOWNLOAD_FAILED_MESSAGE
} from "./PdfReportRenderer";

const ERROR_SNACKBAR_MAP = {
  [TIMEOUT_MESSAGE]:
    "PDF generation took too long. Your data may be large — try again or contact support.",
  [DOWNLOAD_FAILED_MESSAGE]:
    "Your PDF generated but the browser blocked the download. Try clicking the chip again."
};

const errorMessageForSnackbar = (errorMessage) =>
  ERROR_SNACKBAR_MAP[errorMessage] || "Couldn't generate your PDF — try again?";

const STATIC_NEXT_STEPS = [
  "Connect real business data in Aclymate Navigator to raise your Confidence label from Low to High.",
  "Reduce your largest emission source — open Aclymate Explorer for category-specific guidance and benchmarks.",
  "Talk to an Aclymate sustainability expert for a 30-minute orientation."
];

const generateUuid = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `gen-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildDataGapsList = (surveyProgress) => {
  if (!surveyProgress) {
    return [];
  }
  return [
    ...(surveyProgress.skippedSections || []),
    ...(surveyProgress.unknownSections || [])
  ];
};

const buildReportProps = ({
  accountData,
  allEmissions,
  dbEmissions,
  offices,
  rawTransactions,
  year
}) => {
  const { name, vendorSurveys = [], revenueYears = [] } = accountData || {};
  const surveyEmissions = filterEmissionsForYear(allEmissions, year);
  const yearSurveyEntry = findSurveyEntry(vendorSurveys, year);
  const snapshotTotalTons = computeTotalTons({
    yearSurveyEntry,
    revenueYears,
    activeYear: year,
    surveyEmissions
  });
  const totalTons = resolveDisplayTotalTons({
    totalTons: snapshotTotalTons,
    surveyEmissions
  });
  const scopePieSeries = buildScopeSeries(surveyEmissions);
  const sourcePieSeries = buildSourceSeries(surveyEmissions);
  const surveyProgress = yearSurveyEntry
    ? computeSurveyProgress({
        accountData,
        vendorSurveysEntry: yearSurveyEntry,
        hasOffices: (offices || []).length > 0,
        rawTransactions,
        dbEmissions,
        year
      })
    : null;
  const completenessSummary = buildCompletenessSummary(surveyProgress);

  if (!name) {
    Sentry.captureMessage(
      "PDF report generated without a company name (Explorer onboarding gap)",
      { level: "warning", tags: { feature: "explorer-pdf-report" } }
    );
  }

  const safeTotalTons = Number.isFinite(Number(totalTons))
    ? Number(totalTons)
    : 0;

  return {
    companyName: name || "Your Company",
    year,
    generatedDate: dayjs().format("MMMM D, YYYY"),
    totalTons: safeTotalTons,
    scopePieSeries,
    sourcePieSeries,
    completenessSummary,
    dataGapsList: buildDataGapsList(surveyProgress),
    recommendedNextSteps: STATIC_NEXT_STEPS
  };
};

const validateYear = ({
  accountData,
  allEmissions,
  isAccountDataLoading,
  year
}) => {
  if (isAccountDataLoading || !accountData) {
    return "Your data is still loading — give it a second and try again.";
  }
  if (accountData?.stripeCustomerId) {
    return "PDF reports are an Explorer-only feature — open Navigator's report tools instead.";
  }
  if (!year || Number.isNaN(Number(year))) {
    return "We need a year to generate the report.";
  }
  const yearSurveyEntry = findSurveyEntry(
    accountData?.vendorSurveys,
    Number(year)
  );
  if (!yearSurveyEntry) {
    return "We need a completed survey for that year first.";
  }
  const surveyEmissions = filterEmissionsForYear(allEmissions, Number(year));
  const totalTons = computeTotalTons({
    yearSurveyEntry,
    revenueYears: accountData?.revenueYears || [],
    activeYear: Number(year),
    surveyEmissions
  });
  if (!(Number(totalTons) > 0)) {
    return "Your survey is in but we still need revenue for that year.";
  }
  return null;
};

const PLAN_GATE_ERROR_CODES = new Set([
  "platform_account_not_allowed",
  "plan_restriction"
]);

const postAuditStamp = async ({
  companyId,
  year,
  completenessPercent,
  generationId,
  status,
  errorMessage
}) => {
  try {
    const response = await fetchOurApi({
      path: "/reports/explorer-pdf-report",
      method: "POST",
      data: {
        companyId,
        year: Number(year),
        completenessPercent,
        generationId,
        status,
        ...(errorMessage ? { errorMessage } : {})
      },
      callback: (res) => res
    });
    if (response?.error) {
      Sentry.captureMessage(
        `PDF report audit endpoint returned error: ${response.message}`,
        {
          level: "error",
          tags: { feature: "explorer-pdf-report", status },
          extra: { companyId, year, generationId, status }
        }
      );
      return {
        ok: false,
        reason: "endpoint_error",
        message: response.message,
        code: response.code,
        response
      };
    }
    return { ok: true, response };
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "explorer-pdf-report", status },
      extra: { companyId, year, generationId }
    });
    return { ok: false, reason: "network_error", message: err?.message };
  }
};

export const usePdfReportDownload = ({ triggerPath = "scout_card" } = {}) => {
  const [accountData, isAccountDataLoading] = useAccountData();
  const { allEmissions = [] } = useEmissionsContext() || {};
  const [offices] = useCachedDisplayData("offices");
  const [rawTransactions] = useCachedDisplayData("transactions");
  const [dbEmissions, isEmissionsLoading] = useCachedDisplayData("emissions");
  const { activateSnackbar } = useExplorerLayoutContext() || {};

  const [activeYear, setActiveYear] = useState(null);
  const [error, setError] = useState(null);
  const lastYearRef = useRef(null);
  const generationIdRef = useRef(null);
  const inFlightRef = useRef(false);

  const isGenerating = activeYear !== null;

  const notify = useCallback(
    (severity, message) => {
      activateSnackbar?.({ severity, message });
    },
    [activateSnackbar]
  );

  const reportProps = useMemo(() => {
    if (!activeYear) {
      return null;
    }
    return buildReportProps({
      accountData,
      allEmissions,
      dbEmissions,
      offices,
      rawTransactions,
      year: Number(activeYear)
    });
  }, [
    accountData,
    allEmissions,
    dbEmissions,
    offices,
    rawTransactions,
    activeYear
  ]);

  const handleCompleted = useCallback(async () => {
    const year = Number(lastYearRef.current);
    const completenessPercent = reportProps?.completenessSummary?.percent || 0;
    const companyId = getAccountId();
    const generationId = generationIdRef.current || generateUuid();

    trackPdfReportDownloaded({
      companyId,
      year,
      completenessPercent,
      triggerPath
    });

    const auditResult = await postAuditStamp({
      companyId,
      year,
      completenessPercent,
      generationId,
      status: "downloaded"
    });

    const isPlanGate =
      !auditResult.ok && PLAN_GATE_ERROR_CODES.has(auditResult.code);
    if (isPlanGate) {
      notify("warning", auditResult.message);
    } else if (!auditResult.ok) {
      notify(
        "success",
        `Your ${year} report is downloading, but we couldn't record it on our end.`
      );
    } else {
      notify("success", `Your ${year} report is downloading.`);
    }

    setActiveYear(null);
    setError(null);
    generationIdRef.current = null;
    inFlightRef.current = false;
  }, [reportProps, triggerPath, notify]);

  const handleError = useCallback(
    async (err) => {
      try {
        const year = Number(lastYearRef.current);
        const errorMessage =
          (err && (err.message || err.toString())) || "unknown error";
        const companyId = getAccountId();
        const completenessPercent =
          reportProps?.completenessSummary?.percent || 0;
        const generationId = generationIdRef.current || generateUuid();
        await postAuditStamp({
          companyId,
          year,
          completenessPercent,
          generationId,
          status: "failed",
          errorMessage
        });
        trackPdfReportFailed({
          companyId,
          year,
          errorMessage,
          triggerPath
        });
        notify("error", errorMessageForSnackbar(errorMessage));
        setError(errorMessage);
      } finally {
        setActiveYear(null);
        generationIdRef.current = null;
        inFlightRef.current = false;
      }
    },
    [reportProps, triggerPath, notify]
  );

  const trigger = useCallback(
    ({ year } = {}) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      const resolvedYear = Number(year);
      const validationError = validateYear({
        accountData,
        allEmissions,
        isAccountDataLoading,
        year: resolvedYear
      });
      if (validationError) {
        inFlightRef.current = false;
        setError(validationError);
        notify("error", validationError);
        return;
      }
      lastYearRef.current = resolvedYear;
      generationIdRef.current = generateUuid();
      setError(null);
      notify("info", `Preparing your ${resolvedYear} report...`);
      setActiveYear(resolvedYear);
    },
    [accountData, allEmissions, isAccountDataLoading, notify]
  );

  const retry = useCallback(() => {
    const targetYear = lastYearRef.current;
    if (!targetYear) {
      notify(
        "error",
        "We've lost track of which year to retry — pick a year and try again."
      );
      return;
    }
    setError(null);
    trigger({ year: targetYear });
  }, [trigger, notify]);

  const rendererJsx =
    activeYear && reportProps && !isEmissionsLoading ? (
      <PdfReportRenderer
        reportProps={reportProps}
        generationId={generationIdRef.current}
        onCompleted={handleCompleted}
        onError={handleError}
      />
    ) : null;

  return { trigger, isGenerating, error, retry, rendererJsx };
};
